import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  type ForwardedRef,
} from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Separator } from '@wartek-id/separator'
import { Text } from '@wartek-id/text'

import KinerjaDropdownGroup from 'app/KinerjaKSPage/components/KinerjaDropdown/KinerjaDropdownGroup'
import useContentContext from 'app/KinerjaKSPage/hooks/useContentContext'
import useSKPKSTrackEvent from 'app/KinerjaKSPage/hooks/useSKPKSTrackEvent'
import { PANDUAN_PENGELOLAAN_URL } from 'app/KinerjaShared/utils/panduan'

import type { KinerjaSelection, SKPTabHandler } from 'types/kinerja'
import type {
  KinerjaKsPerencanaanTabBaseProps,
  UpdateSKPKSPayloadWithoutNpsn,
} from 'types/kinerja-ks'

import ImgDocument from 'assets/icons/img-document.svg'

export const MIN_POINT = 32

const rules = [
  `Anda diharapkan memiliki minimal ${MIN_POINT} poin per semester untuk memenuhi ekspektasi atasan.`,
  'Jika kurang, mohon diskusikan dengan atasan Anda. Jika lebih, ekspektasi atasan akan semakin lebih baik juga.',
  'Pilihan Rencana Hasil Kerja memiliki poin yang berbeda berdasarkan tingkat keterlibatan dan dampaknya.',
]

interface Tab2Props extends KinerjaKsPerencanaanTabBaseProps {
  customStickyFooter?: () => JSX.Element
  onChange?: (values: KinerjaSelection[]) => void
  allowDeleteSingleValue?: boolean
}

const filterEmptyValues = (value: KinerjaSelection) => value.id !== 0

const Tab2 = (
  {
    skp,
    isSubmitting = false,
    onClickNext = () => {},
    onClickSave = () => {},
    onChange = () => {},
    allowDeleteSingleValue = true,
    customStickyFooter,
  }: Tab2Props,
  ref: ForwardedRef<SKPTabHandler>
) => {
  const { trackClickDraftPlanning, trackClickToPerilakuKerja } =
    useSKPKSTrackEvent()

  const [isDialogConfirmationOpen, setIsDialogConfirmationOpen] =
    useState(false)
  const { contentPengembanganKompetensi, getPengembanganKompetensiById } =
    useContentContext()

  const { setValue, getValues, handleSubmit, watch } = useForm<{
    pengembanganKompetensi: KinerjaSelection[]
  }>({
    defaultValues: {
      pengembanganKompetensi: skp?.pengembanganKompetensi ?? [],
    },
  })

  const selectedValues = watch('pengembanganKompetensi')
  const isDisabledSubmitButton = selectedValues.length === 0

  const totalSelectedPoint = selectedValues.reduce((prev, curr) => {
    const pengembanganKompetensi = getPengembanganKompetensiById(curr.id)
    const currentTotalValue =
      curr.kuantitas * (pengembanganKompetensi?.bobot ?? 0)

    return prev + currentTotalValue
  }, 0)
  const pointsNeeded = MIN_POINT - totalSelectedPoint

  const handleChange = (values: KinerjaSelection[]) => {
    const filteredValues = values.filter(filterEmptyValues)
    setValue('pengembanganKompetensi', filteredValues)

    onChange(filteredValues)
  }

  // Field `npsn` can be safely ignored since it will be automatically added to the payload.
  const generatePayload = useCallback((): UpdateSKPKSPayloadWithoutNpsn => {
    return {
      pengembanganKompetensi: getValues('pengembanganKompetensi').filter(
        (k) => k.id > 0
      ), // Only include the valid ones,
    }
  }, [getValues])

  const onSubmit = () => {
    if (pointsNeeded > 0) {
      setIsDialogConfirmationOpen(true)
    } else {
      submitForm()
    }
  }

  const submitForm = () => {
    trackClickToPerilakuKerja({
      kinerjaPlanningId: skp.id,
      kinerjaPlanningStatus: skp.status,
      screenName: 'KINERJA_PLANNING_KS_PENGEMBANGAN_KOMPETENSI_PAGE',
    })

    onClickNext(generatePayload())
  }

  const onSaveDraft = useCallback(() => {
    trackClickDraftPlanning({
      kinerjaPlanningId: skp.id,
      kinerjaPlanningStatus: skp.status,
      screenName: 'KINERJA_PLANNING_KS_PENGEMBANGAN_KOMPETENSI_PAGE',
    })

    onClickSave(generatePayload())
  }, [onClickSave, generatePayload, trackClickDraftPlanning, skp])

  useImperativeHandle(
    ref,
    () => ({
      saveDraft: onSaveDraft,
    }),
    [onSaveDraft]
  )

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-9">
          <div className="flex gap-3">
            <ImgDocument className="w-16 h-16 shrink-0" />
            <div>
              <Text variant="display-sm">Pengembangan Kompetensi</Text>
              <Text className="mt-1.5" color="subdued">
                Pilih jenis Pengembangan Kompetensi yang paling efektif dan
                berdampak untuk diri Anda.
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-9 mt-9">
            <div className="border border-gray-30 rounded">
              <div className="p-4 pb-5">
                <div className="flex gap-1">
                  <Icon>error</Icon>
                  <Text variant="action">
                    Berdasarkan peraturan yang berlaku:
                  </Text>
                </div>
                <ul className="list-disc list-inside pl-2">
                  {rules.map((rule, i) => (
                    <li key={`rule-${i}`} className="list-item">
                      <Text color="subdued" as="span">
                        {rule}
                      </Text>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-blue-5 py-2 pl-7 pr-3 flex justify-between">
                <div className="flex gap-8 items-center">
                  <Text
                    variant="action"
                    color="primary"
                    className="uppercase"
                    data-testid="total-point"
                  >
                    Total poin: {totalSelectedPoint}
                  </Text>

                  {pointsNeeded > 0 && (
                    <div className="bg-[#FEF4F4] border border-red-60 rounded p-1">
                      <Text
                        variant="helper"
                        color="critical"
                        data-testid="points-needed"
                      >
                        Kurang {pointsNeeded} poin
                      </Text>
                    </div>
                  )}
                </div>

                <a
                  href={PANDUAN_PENGELOLAAN_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Button variant="ghost">
                    <Icon color="informational" className="mr-1">
                      help
                    </Icon>{' '}
                    Bagaimana poin ini dihitung?
                  </Button>
                </a>
              </div>
            </div>

            <KinerjaDropdownGroup
              content={contentPengembanganKompetensi}
              kinerja={skp?.pengembanganKompetensi ?? []}
              onChange={handleChange}
              allowDeleteSingleValue={allowDeleteSingleValue}
              showPoint
            />
          </div>
        </div>

        <Separator />

        <div className="px-8 py-4 flex gap-5 justify-end">
          {customStickyFooter ? (
            customStickyFooter()
          ) : (
            <>
              <Button
                size="lg"
                color="white"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                onClick={onSaveDraft}
              >
                Simpan draf
              </Button>
              <Button
                size="lg"
                color="black"
                isLoading={isSubmitting}
                disabled={isSubmitting || isDisabledSubmitButton}
                type="submit"
              >
                Ke Perilaku Kerja
              </Button>
            </>
          )}
        </div>
      </form>

      <Dialog isOpen={isDialogConfirmationOpen}>
        <DialogOverlay />
        <DialogContent
          className="p-6"
          data-testid="popup-min-point-confirmation"
        >
          <Text variant="display-sm">
            Pengembangan Kompetensi kurang {pointsNeeded} poin. Tetap lanjut?
          </Text>
          <Text variant="body-lg" color="subdued" className="mt-1">
            Tambah poin sekarang agar sesuai dengan Ekspektasi Pimpinan. Jika
            lanjut, atasan akan menyesuaikan nanti.
          </Text>

          <div className="flex gap-4 mt-4">
            <Button
              size="lg"
              color="white"
              disabled={isSubmitting}
              isLoading={isSubmitting}
              onClick={() => submitForm()}
            >
              Lanjut
            </Button>
            <Button
              size="lg"
              disabled={isSubmitting}
              isLoading={isSubmitting}
              onClick={() => setIsDialogConfirmationOpen(false)}
            >
              Tambah poin
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default forwardRef<SKPTabHandler, Tab2Props>(Tab2)
